import { useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Fetches active custom_scripts from Supabase and injects them into <head> or <body>.
 * Scripts are cleaned up on unmount to avoid duplicates on re-render.
 */
export function DynamicScripts() {
  const { data: scripts } = useQuery({
    queryKey: ["dynamic-scripts"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("custom_scripts")
        .select("id, script_tag, placement")
        .eq("is_active", true);
      if (error) throw error;
      return data;
    },
    staleTime: 5 * 60 * 1000, // cache for 5 min
  });

  useEffect(() => {
    if (!scripts?.length) return;

    const injected: HTMLElement[] = [];

    scripts.forEach((s) => {
      // Parse the script_tag HTML string into DOM nodes
      const wrapper = document.createElement("div");
      wrapper.innerHTML = s.script_tag.trim();

      const nodes = Array.from(wrapper.childNodes);
      const target = s.placement === "body" ? document.body : document.head;

      nodes.forEach((node) => {
        if (node instanceof HTMLScriptElement) {
          // Browsers won't execute scripts inserted via innerHTML, so recreate them
          const script = document.createElement("script");
          Array.from(node.attributes).forEach((attr) =>
            script.setAttribute(attr.name, attr.value)
          );
          if (node.textContent) script.textContent = node.textContent;
          script.dataset.customScriptId = s.id;
          target.appendChild(script);
          injected.push(script);
        } else if (node instanceof HTMLElement) {
          node.dataset.customScriptId = s.id;
          target.appendChild(node);
          injected.push(node);
        }
      });
    });

    return () => {
      injected.forEach((el) => el.remove());
    };
  }, [scripts]);

  return null;
}
